import { computed, ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { type IOrder, MARKET_SEGMENTS } from '@rogue/universal'
import type { Address, CreditCard, IRogue } from '../services/RogueTypes'
import { fetchOrders } from '../helpers/fetchOrders'
import { listProductIds } from '../helpers/buy-request'
import { subscribeToNewsletters } from '../helpers/newsletter-subscribe'
import type { CustomerStoreState, EarnedProduct, SessionResponse, Ticket, UpdateCustomerRequest } from './customerTypes'
import { useProductStore } from './productStore'
import { useListStore } from '@/js/stores/listStore'
import { usePromoStore } from '@/js/stores/promoStore'
import { canSkipRefresh } from '@/js/app-bridge/helpers'
import { messages } from '@/js/app-bridge/messages'
import { topCustomCategories, bottomCustomCategories, defaultMiscellaneousBanners, programmingBanner } from '@/js/app-bridge/content/banners'
import type { Newsletter } from '@/js/helpers/newsletter-list'

const defaultState: CustomerStoreState = {
  _id            : null,
  loading        : true,
  addresses      : [],
  groupId        : 0,
  tier           : null,
  email          : null,
  rogueId2       : null,
  segments       : [],
  telephone      : '',
  occupations    : [],
  newsletters    : [],
  primarySegment : 'None',
  firstname      : '',
  lastname       : '',
  orders         : [],
  tickets        : [],
  earnedProducts : {},
  creditCards    : [],
  bins           : [],
  sheerId        : null,
  gym            : null,
  country        : null,
  gender         : null,
  birthdate      : null,
}

const DEFAULT_EXPIRES = 300

// Sort segments by Crossfit (US, CA, AU) > Powerlifting (US, CA, AU) > Olympic (US, CA, AU) > Strongman (US, CA, AU)
const SEGMENT_SORT:number[] = [11, 43, 22, 12, 44, 23, 13, 45, 24, 19, 51, 30]

/**
 * Put default shipping at top, then default billing
 * @param a {address}
 * @param b {address}
 */
function addressSort(a:Address, b: Address) {
  if (a.default_shipping) {
    return -1
  }
  if (b.default_shipping) {
    return 1
  }

  return 0
}

export const useCustomerStore = defineStore('customers', () => {
  const listStore = useListStore()
  const promoStore = usePromoStore()

  let initPromise: Promise<void>
  let loadPromise: Promise<void> | undefined
  let cardPromise: Promise<void> | undefined
  let orderPromise: Promise<void> | undefined

  const _id = ref<null | string>(defaultState._id)
  const loading = ref<boolean>(defaultState.loading)
  const addresses = ref<Address[]>(defaultState.addresses)
  const groupId = ref<number>(-1)
  const tier = ref<null | string>(defaultState.tier)
  const email = ref<null | string>(defaultState.email)
  const rogueId2 = ref<null | string>(defaultState.rogueId2)
  const segments = ref<number[]>(defaultState.segments)
  const occupations = ref<number[]>(defaultState.occupations)
  const newsletters = ref<number[]>(defaultState.newsletters)
  const primarySegment = ref<string>(defaultState.primarySegment)
  const firstname = ref<string>(defaultState.firstname)
  const lastname = ref<string>(defaultState.lastname)
  const telephone = ref<string>(defaultState.telephone)
  const orders = ref<IOrder[]>(defaultState.orders)
  const tickets = ref<Ticket[]>(defaultState.tickets)
  const earnedProducts = ref(defaultState.earnedProducts)
  // Only earned products that still have redemptions left. Depleted tickets
  // must not affect pricing or discounts anywhere they're consumed.
  const activeEarnedProducts = computed<CustomerStoreState['earnedProducts']>(() =>
    Object.fromEntries(
      Object.entries(earnedProducts.value).filter(([, earned]) => earned.remainingUses >= 1)
    )
  )
  const creditCards = ref(defaultState.creditCards)
  const bins = ref(defaultState.bins)
  const sheerId = ref<CustomerStoreState['sheerId']>(defaultState.sheerId)

  // prodile fields used for Strongest challenge registration
  const gym = ref<string | null>(defaultState.gym)
  const country = ref<string | null>(defaultState.country)
  const gender = ref<'Male' | 'Female' | null>(defaultState.gender)
  const birthdate = ref<string | null>(defaultState.birthdate)

  const loggedIn = computed<boolean>(() => groupId.value > 0)
  const defaultShippingAddress = computed<Address | undefined>(() => addresses.value.find(address => address.isDefaultShipping))
  const defaultBillingAddress = computed<Address | undefined>(() => addresses.value.find(address => address.isDefaultBilling) || defaultShippingAddress.value)
  const defaultCreditCard = computed(() => creditCards.value.find(cc => cc.isDefault))
  const currentState = computed<CustomerStoreState>(() => ({
    _id            : _id.value,
    loading        : loading.value,
    addresses      : addresses.value,
    groupId        : groupId.value,
    tier           : tier.value,
    email          : email.value,
    rogueId2       : rogueId2.value,
    segments       : segments.value,
    occupations    : occupations.value,
    newsletters    : newsletters.value,
    primarySegment : primarySegment.value,
    firstname      : firstname.value,
    lastname       : lastname.value,
    telephone      : telephone.value,
    orders         : orders.value,
    tickets        : tickets.value,
    earnedProducts : earnedProducts.value,
    creditCards    : creditCards.value,
    bins           : bins.value,
    sheerId        : sheerId.value,
    gym            : gym.value,
    country        : country.value,
    gender         : gender.value,
    birthdate      : birthdate.value,
  }))

  function clearCustomer(rogue: IRogue) {
    rogue.log.set('user', currentState.value)

    loading.value = true
    _id.value = defaultState._id
    addresses.value = defaultState.addresses
    groupId.value = defaultState.groupId
    email.value = defaultState.email
    rogueId2.value = defaultState.rogueId2
    segments.value = defaultState.segments
    occupations.value = defaultState.occupations
    newsletters.value = defaultState.newsletters
    primarySegment.value = defaultState.primarySegment
    firstname.value = defaultState.firstname
    lastname.value = defaultState.lastname
    telephone.value = defaultState.telephone
    orders.value = defaultState.orders
    tickets.value = defaultState.tickets
    earnedProducts.value = defaultState.earnedProducts
    creditCards.value = defaultState.creditCards
    bins.value = defaultState.bins
    sheerId.value = defaultState.sheerId
    loading.value = false
    gym.value = defaultState.gym
    country.value = defaultState.country
    gender.value = defaultState.gender
    birthdate.value = defaultState.birthdate

    if (typeof window !== 'undefined') {
      const w = window as any
      if (w.cnstrc) {
        delete w.cnstrc.userId
        delete w.cnstrc.userSegments
      }
      // Clear segments already pushed to the live SDK so logout doesn't leak the prior user's personalization.
      rogue.cnstrc.setSegments([]).catch(err => rogue.log.warn('Failed to clear Constructor segments on logout', err))
    }
  }

  function setCreditCards(cards: CreditCard[]) {
    cards?.forEach(card => {
      card.expired = new Date(card.expYear, card.expMonth, 0) < new Date()
    })
    creditCards.value = cards?.sort((a, b) => {
      if (a.isDefault) {
        return -1
      }
      if (b.isDefault) {
        return 1
      }

      return 0
    })
  }

  function syncConstructorSegments(rogue: IRogue) {
    if (typeof window === 'undefined') {
      return
    }
    const w = window as any
    w.cnstrc = w.cnstrc || {}
    const interests = MARKET_SEGMENTS[rogue.site.id]?.Interests || {}
    const userSegments: string[] = segments.value
      .map(id => Object.keys(interests).find(key => interests[key] === id))
      .filter(Boolean) as string[]
    if (gender.value) {
      userSegments.push(gender.value)
    }
    // Push to the live Constructor SDK (it reads window.cnstrc only at init); [] clears stale segments.
    rogue.cnstrc.setSegments(userSegments).catch(err => rogue.log.warn('Failed to sync Constructor segments', err))
    if (userSegments.length) {
      w.cnstrc.userSegments = userSegments
    } else {
      delete w.cnstrc.userSegments
    }
  }

  function updateCustomerFields(fields: Partial<CustomerStoreState & {group: number}>, rogue: IRogue) {
    if (fields.addresses && Array.isArray(fields.addresses) && fields.addresses.length) {
      fields.addresses.sort(addressSort)
      // @ts-ignore
      fields.addresses.forEach(a => a.vatNumber = a.vatId)
    }
    if (fields.group && !fields.groupId) {
      fields.groupId = fields.group
    }
    if (fields._id && fields.email) {
      rogue.log.set('user', { id: fields._id, email: fields.email })
    }

    if (fields.segments) {
      const primarySegmentId = fields.segments
        // Only care about segments included in SEGMENT_SORT
        .filter(id => SEGMENT_SORT.indexOf(Number(id)) !== -1)
        // Sort by the highest priority
        .sort((a, b) => SEGMENT_SORT.indexOf(a) - SEGMENT_SORT.indexOf(b))[0]

      primarySegment.value = Object.keys(MARKET_SEGMENTS[rogue.site.id].Interests)
        .find(key => MARKET_SEGMENTS[rogue.site.id].Interests[key] === primarySegmentId) || 'None'
    }

    if (fields._id) {
      _id.value = fields._id
    }
    
    if (fields.rogueId2) {
      rogueId2.value = fields.rogueId2
      if (typeof window !== 'undefined') {
        const w = window as any
        w.cnstrc = w.cnstrc || {}
        w.cnstrc.userId = fields.rogueId2
      }
    }

    if (fields.addresses) addresses.value = fields.addresses
    if (fields.groupId) groupId.value = fields.groupId
    if (fields.tier) tier.value = fields.tier
    if (fields.email) email.value = fields.email
    if (fields.segments) segments.value = fields.segments
    if (fields.occupations) occupations.value = fields.occupations
    if (fields.primarySegment) primarySegment.value = fields.primarySegment
    if (fields.firstname) firstname.value = fields.firstname
    if (fields.lastname) lastname.value = fields.lastname
    if (fields.telephone) telephone.value = fields.telephone
    if (fields.orders) orders.value = fields.orders
    if (fields.tickets) tickets.value = fields.tickets
    if (fields.earnedProducts) earnedProducts.value = fields.earnedProducts
    if (fields.creditCards) creditCards.value = fields.creditCards
    if (fields.bins) bins.value = fields.bins
    if (fields.sheerId) sheerId.value = fields.sheerId
    if (fields.gym) gym.value = fields.gym
    if (fields.country) country.value = fields.country
    if (fields.gender) gender.value = fields.gender
    if (fields.birthdate) birthdate.value = fields.birthdate

    if (fields.segments !== undefined || fields.gender !== undefined) {
      syncConstructorSegments(rogue)
    }

    if (Object.keys(fields).length > 1) {
      loading.value = false
      rogue.track.identify()
    }
  }

  async function loadAppConfig(rogue: IRogue) {
    if (!rogue.appContext) {
      return
    }

    const config: any = {
      miscellaneousBanners : defaultMiscellaneousBanners,
      programs             : [],
      // Site migration is permanent; the native app still reads these keys
      allToCom             : true,
      ukSiteEnabled        : true,
      addGymEnabled        : true,
      topCategories        : topCustomCategories[rogue.site.code],
      bottomCategories     : bottomCustomCategories[rogue.site.code],
      isSpaAuthenticated   : loggedIn.value,
      requirements         : rogue.featureFlags.eval('app_requirements'),
      integrations         : rogue.featureFlags.eval('app_integrations'),
    }

    try {
      if (loggedIn.value) {
        const programs = await rogue.fetch<unknown[]>('/svc_programming/programs')
        if (programs.length > 0) {
          config.miscellaneousBanners = { programming: programmingBanner, ...config.miscellaneousBanners }
          config.programs = programs
        }
      }
    } catch (e) {
      rogue.log.warn('Error loading app config', e as Error)
    }

    rogue.emitToApp(messages.APP_CONFIG, config)
  }

  async function loadEarnedStore(rogue: IRogue) {
    if (groupId.value) {
      try {
        const loadedtickets = await rogue.fetch<EarnedProduct[]>('/svc_ticket/tickets')
        const earned:CustomerStoreState['earnedProducts']  = {}

        loadedtickets
          .filter(t => t.product)
          .forEach(t => {
            const { discount, productId } = t.product
            const { value, type = 'percent', freeShipping } = discount

            if (productId) {
              earned[productId] = {
                remainingUses : t.remainingUses,
                orderId       : t.orderId,
                _id           : t._id,
                identifier    : t.ticketIdentifier,
                value,
                type,
                freeShipping,
              }
            }
          })

        earnedProducts.value = earned
        if (rogue.loaded.checkout) {
          const { updateOrderDiscounts } = await import('../pages/checkout/checkout')
          updateOrderDiscounts(rogue)
        }
      } catch(e) {
        rogue.log.info('Error loading tickets', e)
      }
    }
  }

  async function loadNewsletters(rogue: IRogue): Promise<void> {
    if (!loggedIn.value) {
      return
    }

    try {
      await rogue.fetch<{ emailListIds: number[], unsubscribedMessageTypeIds: number[] }>(`/svc_ssr/newsletters/site/${rogue.site.code}/subscriptions`, 'GET').then(subscriptions => {
        newsletters.value = subscriptions?.emailListIds || []
      })
    } catch (e) {
      rogue.log.warn('Failure to fetch user subscriptions', e)
    }
  }

  async function updateSubscriptions(rogue: IRogue, newsletterSelections: Newsletter[]) {
    const subscribeIds: number[] = []
    const unsubscribeIds: number[] = []

    newsletterSelections.forEach(option => {
      if (option.selected && !newsletters.value.includes(option.id)) {
        subscribeIds.push(option.id)
      } else if (!option.selected && newsletters.value.includes(option.id)) {
        unsubscribeIds.push(option.id)
      }
    })

    const subscribe = {
      email       : email.value || '',
      userId      : _id.value,
      newsletters : subscribeIds,
    }

    if (subscribeIds.length > 0) {
      await subscribeToNewsletters(rogue, subscribe)
      newsletters.value = newsletters.value.concat(subscribeIds)
    }
    // captcha will bounce the request as a duplicate if we use the same captcha token as we used for subscribe
    const unsubscribe = {
      email       : email.value,
      userId      : _id.value,
      newsletters : unsubscribeIds,
    }

    if (unsubscribeIds.length > 0) {
      await rogue.fetch('/svc_ssr/newsletters/unsubscribe', 'POST', unsubscribe)
      newsletters.value = newsletters.value.filter(value => !unsubscribeIds.includes(value))
    }
  }

  async function loadCards(rogue: IRogue, force: boolean = false) {
    if (!loggedIn.value) {
      return Promise.resolve()
    }
    if (!cardPromise || force) {
      cardPromise = rogue.fetch<{credit_cards: CreditCard[]}>('/svc_customer/customer/credit-cards')
        .then(res => {
          setCreditCards(res?.credit_cards)
        })
    }

    return Promise.resolve(cardPromise)
  }

  async function loadBins(rogue: IRogue) {
    try {
      const loadedBins = await rogue.fetch<CustomerStoreState['bins']>('/svc_bin/bin')
      bins.value = loadedBins
    } catch (e) {
      rogue.log.warn('Error loading bins', e as Error)
    }
  }

  async function loadOrders(rogue: IRogue) {
    if (!loggedIn.value) {
      return Promise.resolve()
    }
    if (!orderPromise) {
      orderPromise = fetchOrders(rogue)
        .then(async res => {
          orders.value = res?.orders
          const allOrderItems = res?.orders.flatMap(order => order.items)
          const uniqueProductIds = [...new Set(listProductIds(allOrderItems))]
          if (uniqueProductIds.length) {
            const productStore = useProductStore(rogue.pinia)
            await productStore.loadAll(uniqueProductIds, rogue)
          }
        })
    }

    return Promise.resolve(orderPromise)
  }

  async function loadCustomer(rogue: IRogue) {
    if (!loggedIn.value) {
      return Promise.resolve()
    }

    if (!loadPromise) {
      loadPromise = rogue.fetch<Partial<CustomerStoreState & {group: number}>>('/svc_customer/customer').then(customer => {
        if (customer.groupId === undefined && customer.group !== undefined) {
          customer.groupId = customer.group
        }
        updateCustomerFields(customer, rogue)
        rogue.cnstrc.init(rogue)
        rogue.track.identify()
      })
    }

    return Promise.resolve(loadPromise)
  }

  async function logout(rogue: IRogue) {
    // Forget stored payment methods when a customer logs out
    import('@/js/stores/paymentMethodStore').then(pms => {
      const paymentMethodStore = pms.usePaymentMethodStore(rogue.pinia)
      paymentMethodStore.$reset()
    })
    await rogue.fetch('/svc_session/session/logout', 'POST')
    rogue.log.crumb('customer', 'Logged out')
    clearCustomer(rogue)

    orderPromise = undefined
    cardPromise = undefined
    loadPromise = undefined

    loadAppConfig(rogue)
    listStore.handleCustomerChange(rogue, null, null)
    promoStore.reloadCustomerPricing(rogue)
    rogue.track.track({ type: 'logout' })
    rogue.track.identify()
    rogue.cnstrc.init(rogue)
  }

  function refresh(secs: number, rogue: IRogue) {
    setTimeout(() => {
      if (canSkipRefresh(rogue)) {
        // Skip session refresh process for the app: in webviews that are used as tabs / if the app is in background
        return
      }
      rogue.fetch<SessionResponse>('/svc_session/session')
        .then(resp => {
          if (resp.exp) {
            const exp = resp.exp || DEFAULT_EXPIRES
            refresh(exp - 45, rogue)
            rogue.log.crumb('auth', 'session prolonged')
          } else if (resp.cg && resp.cg !== 0) {
            refresh(DEFAULT_EXPIRES - 45, rogue)
            rogue.log.crumb('auth', 'session prolonged (no exp)')
          } else {
            rogue.log.crumb('auth', 'no cg on extend?')
          }
        })
        .catch(err => {
          rogue.emitToApp('webview_error', { error: err })
          rogue.log.error('Error extending session - logging out', err)
          logout(rogue)
        })
    }, secs * 1000)
  }

  async function login(loginEmail: string, password: string, isNewCustomer: boolean, rogue: IRogue) {
    const loginData = { email: loginEmail, password }

    if (loginEmail === email.value && groupId.value > 0) {
      // Dont try to log in the same customer again
      return
    }

    const loginResponse = await rogue.fetchWithCaptcha<any>('/svc_session/session/login', 'POST', loginData, {}, 'json', 'login')
    rogue.log.crumb('customer', 'Logged in')

    import('@/js/stores/paymentMethodStore').then(pms => {
      const paymentMethodStore = pms.usePaymentMethodStore(rogue.pinia)
      paymentMethodStore.$reset()
    })

    updateCustomerFields(loginResponse.customer, rogue)
    loadBins(rogue)

    refresh(DEFAULT_EXPIRES - 45, rogue)

    const loadPromises = [
      loadCustomer(rogue),
      loadCards(rogue),
      loadAppConfig(rogue),
      listStore.handleCustomerChange(rogue, email.value, _id.value),
      promoStore.reloadCustomerPricing(rogue),
      loadEarnedStore(rogue),
    ]

    // Await the customer change stuff synchronously - we want to make sure the checkout store can reinitialize as soon as this Login promise is resolved
    await Promise.all(loadPromises).then(() => {
      if (!isNewCustomer) {
        // @ts-ignore
        rogue.track.track({ type: 'login', email: loginEmail })
      }
    })
  }

  async function register(registerData: {
    email: string,
    firstname: string,
    lastname: string,
    // Filled in below from the customer's existing subscriptions when they have any
    newsletters?: string[],
    password: string,
    password_confirmation: string,
    segments: string[],
  }, rogue: IRogue) {
    if (newsletters.value.length) {
      registerData.newsletters = newsletters.value.map(n => n.toString())
    }

    if (segments.value.length) {
      registerData.segments = segments.value.map(s => s.toString())
    }
    rogue.log.crumb('customer', 'Registered new account')

    rogue.track.track({
      type        : 'register',
      email       : registerData.email,
      newsletters : registerData.newsletters,
      segments    : registerData.segments,
    })

    await rogue.fetchWithCaptcha('/svc_customer/customer', 'POST', registerData, {}, 'json', 'register')

    await login(registerData.email, registerData.password, true, rogue)
  }

  async function removeBin(binId: string, rogue: IRogue) {
    await rogue.fetch(`/svc_bin/bin/${binId}`, 'DELETE')
    const newBins = bins.value.filter(b => b.id !== binId)
    bins.value = newBins
  }

  async function binSignup(binEmail: string, productId: number, parentId: number | undefined, rogue: IRogue) {
    binEmail = binEmail || email.value || ''

    if (!binEmail) {
      return
    }

    if (binEmail && !email.value) {
      updateCustomerFields({ email: binEmail }, rogue)
    }

    if (bins.value.find(b => b.product_id === productId)) {
      return
    }

    try {
      await rogue.fetch('/svc_bin/bin', 'POST', {
        product_id : productId,
        email      : binEmail,
        parent_id  : parentId,
      })
    } catch (e) {
      // @ts-ignore
      if (e.error !== 'validation_error_already_subscribed') {
        throw e
      }
    }

    if (loggedIn.value) {
      await loadBins(rogue)
    } else {
      bins.value = bins.value.concat({ product_id: productId })
    }
  }

  async function init(rogue: IRogue, force = false, reloadEarnedStore = false) {
    if (rogue.ssr) {
      return
    }

    if (!initPromise || force) {
      rogue.log.crumb('customer', `Customer init started ${force ? '(Forced)' : ''}`)
      initPromise = rogue.fetch<SessionResponse>('/svc_session/session')
        .catch(err => {
          rogue.log.warn('Error getting session (attempt 1)', err)

          return rogue.fetch<SessionResponse>('/svc_session/session').catch(err2 => {
            rogue.log.error('Second error getting session - invalid cookies persisting?', err2)
            rogue.log.crumb('customer', 'No session available, failed twice calling to svc_session')

            return { cg: 0, exp: undefined, groupId: undefined, tier: undefined }
          })
        })
        .then(sessionData => {
          rogue.log.crumb('customer', `Customer init finished: ${sessionData.cg}`)
          if (sessionData.cg === 0) {
            // Guest session
            clearCustomer(rogue)

            if (typeof window !== 'undefined') {
              try {
                const savedData = JSON.parse(window.localStorage.getItem('cust_dat') || '{}')
                updateCustomerFields(savedData, rogue)
              } catch (e) {
                //
              }
            }
            rogue.track.identify()
          } else {
            refresh((sessionData.exp || DEFAULT_EXPIRES) - 45, rogue)
            // At this point we know the session is valid, so we don't have to await loading the customer
            updateCustomerFields({ groupId: sessionData.groupId || sessionData.cg || 1, tier: sessionData.tier }, rogue)
            loadCustomer(rogue)
            loadCards(rogue)
            loadBins(rogue)

            loadEarnedStore(rogue).catch(err => {
              rogue.log.warn('Error loading earned store', err)
            })

            if (typeof window !== 'undefined') {
              window.localStorage.removeItem('cust_dat')
            }
          }
          loadAppConfig(rogue)
        })
    } else if (reloadEarnedStore) {
      await loadEarnedStore(rogue).catch(err => {
        rogue.log.warn('Error reloading earned store', err)
      })
    }
    await Promise.resolve(initPromise)
  }

  async function updateCustomer(payload: UpdateCustomerRequest, rogue: IRogue) {
    const customer = await rogue.fetch<{customer: Partial<CustomerStoreState>}>('/svc_customer/customer/', 'PATCH', payload)
    updateCustomerFields(customer.customer, rogue)
    await init(rogue, true)
    rogue.track.identify()
  }

  async function saveSegments(segmentIds: number[], rogue: IRogue) {
    if (loggedIn.value) {
      try {
        await rogue.fetch('/svc_customer/customer/segments', 'PUT', segmentIds)
      } catch (e) {
        // noop
      }
    } else {
      window.localStorage.setItem('cust_dat', JSON.stringify(
        { segments: segmentIds }
      ))
    }
    updateCustomerFields({ segments: segmentIds }, rogue)
  }

  async function forgotPassword(resetEmail: string, rogue: IRogue) {
    const data = { email: resetEmail }
    const resetResponse = await rogue.fetchWithCaptcha<any>('/svc_customer/customer/forgot-password', 'POST', data, {}, 'json', 'forgotpassword')
    rogue.track.track({ type: 'forgotPass', data })

    if (!resetResponse.success) {
      throw resetResponse
    }
  }

  async function resetPassword(data: {
    token : string,
    password : string,
    password_confirmation : string,
  }, rogue: IRogue) {
    try {
      await rogue.fetch('/svc_customer/customer/forgot-password/reset', 'POST', data)
      rogue.track.track({ type: 'resetPass', status: 'success' })
    } catch (e) {
      rogue.track.track({ type: 'resetPass', status: 'error' })
      throw e
    }
  }

  async function saveAddress(address: Address, rogue: IRogue) {
    let newAddresses = [...addresses.value]
    const addressIdx = address._id ? newAddresses.findIndex(a => a._id === address._id) : -1
    const method = addressIdx !== -1 ? 'PATCH' : 'POST'

    let url = '/svc_customer/customer/address'

    if (method === 'PATCH') {
      url += `/${address._id}`
    }

    // @ts-ignore
    address.vatId = address.vatNumber

    const resp = await rogue.fetch<Address>(url, method, address)

    // @ts-ignore
    resp.vatNumber = resp.vatId

    if (resp.isDefaultShipping || resp.isDefaultBilling) {
      newAddresses = newAddresses.map((a) => ({
        ...a,
        isDefaultShipping : resp.isDefaultShipping ? false : a.isDefaultShipping,
        isDefaultBilling  : resp.isDefaultBilling ? false : a.isDefaultBilling,
      }))
    }

    if (method === 'POST') {
      newAddresses.push(resp)
    } else {
      newAddresses.splice(addressIdx, 1, address)
    }

    if (address.isDefaultShipping) {
      rogue.updateShipTo(address.countryId)
      rogue.track.track({ type: 'setDefaultAddress', address })
    }

    updateCustomerFields({ addresses: newAddresses }, rogue)

    // This is not ideal, but is very helpful for any form that just created this address
    return resp
  }

  async function deleteAddress(addressId: string, rogue: IRogue) {
    await rogue.fetch(`/svc_customer/customer/address/${addressId}`, 'DELETE')
    const filteredAddresses = [...addresses.value].filter(a => a._id !== addressId)
    updateCustomerFields({ addresses: filteredAddresses }, rogue)
  }

  async function saveCreditCard(card: CreditCard, rogue: IRogue) {
    if (!loggedIn.value) {
      return card
    }

    if (!card.billingAddressId && card.billingAddress) {
      // @ts-ignore
      if (!card.billingAddress._id) {
        card.billingAddress = await saveAddress(card.billingAddress, rogue)
      }
      // @ts-ignore
      card.billingAddressId = card.billingAddress._id
    }

    // If this is the default, we need to un-default the previous default
    const currentDefault = creditCards.value.find(cc => cc.isDefault)

    if (card.isDefault && currentDefault && currentDefault._id !== card._id) {
      currentDefault.isDefault = false
      await rogue.fetch(`/svc_customer/customer/credit-cards/${currentDefault._id}`, 'put', currentDefault)
    }

    // Update existing card
    if (card._id) {
      await rogue.fetch(`/svc_customer/customer/credit-cards/${card._id}`, 'put', card)
      await loadCards(rogue, true)

      return card
    }

    /**
     * Creating New Card
     * When creating a card, we do not get the _id... so we need to force reload all cards and then find the new one
     */

    // If we dont have a default, this one becomes the default
    card.isDefault = card.isDefault || !currentDefault

    // Keep track of the currently saved cards
    const existingIds = creditCards.value.map(cc => cc._id)

    // Create the new card
    await rogue.fetch('/svc_customer/customer/credit-cards', 'post', card)

    // Force reload all cards
    await loadCards(rogue, true)

    // Find the new one
    return creditCards.value.find(cc => existingIds.indexOf(cc._id) === -1)
  }

  async function deleteCreditCard(card: CreditCard, rogue: IRogue) {
    const res = await rogue.fetch<{success: boolean}>(`/svc_customer/customer/credit-cards/${card._id}`, 'DELETE')
    if (res.success) {
      const cards = [...creditCards.value].filter(c => c._id !== card._id)
      setCreditCards(cards)
    }
  }

  return {
    _id,
    loading,
    addresses,
    groupId,
    tier,
    email,
    rogueId2,
    segments,
    occupations,
    newsletters,
    primarySegment,
    firstname,
    lastname,
    telephone,
    orders,
    tickets,
    earnedProducts,
    activeEarnedProducts,
    creditCards,
    bins,
    sheerId,
    gym,
    country,
    gender,
    birthdate,
    loggedIn,
    defaultShippingAddress,
    defaultBillingAddress,
    defaultCreditCard,
    loadAppConfig,
    logout,
    register,
    loadEarnedStore,
    login,
    loadCards,
    loadNewsletters,
    loadOrders,
    updateSubscriptions,
    removeBin,
    binSignup,
    loadCustomer,
    init,
    updateCustomer,
    saveSegments,
    forgotPassword,
    resetPassword,
    saveAddress,
    deleteAddress,
    saveCreditCard,
    deleteCreditCard,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCustomerStore, import.meta.hot))
}
